import { IconType } from 'react-icons'
import {
  PiChartBar,
  PiChartLine,
  PiChartPieSlice,
  PiConfetti,
  PiFunnel,
  PiGauge,
  PiHandshake,
  PiListBullets,
  PiSealCheck,
  PiSquaresFour,
  PiTable,
  PiTarget,
  PiTrophy,
  PiUsersThree,
} from 'react-icons/pi'
import { ReportIconKey, ViewConfig } from '../view-config'

/**
 * Maps a report/dashboard's semantic `icon` key to a Phosphor icon. Report
 * cards key the glyph off the report *type* — chart=bar, line=time series,
 * table=raw list, metric=single number, target=KPI (number vs a goal) — so the
 * icon reads as "what kind of report is this". Dashboards keep title glyphs
 * (users, handshake) since a dashboard has no single type.
 */
export const REPORT_ICONS: Record<ReportIconKey, IconType> = {
  chart: PiChartBar,
  line: PiChartLine,
  list: PiListBullets,
  funnel: PiFunnel,
  target: PiTarget,
  trophy: PiTrophy,
  check: PiSealCheck,
  celebrate: PiConfetti,
  handshake: PiHandshake,
  pie: PiChartPieSlice,
  users: PiUsersThree,
  table: PiTable,
  metric: PiGauge,
  dashboard: PiSquaresFour,
}

/** The coarse report types the library groups by (mirrors the card glyphs). */
export type ReportType = 'metric' | 'chart' | 'table' | 'goal'

/** Human label per report type, for the library table's Type column / pills. */
export const REPORT_TYPE_LABEL: Record<ReportType, string> = {
  metric: 'Metric',
  chart: 'Chart',
  table: 'Table',
  goal: 'Goal',
}

/**
 * Coarse type of a report, by shape — gauge = metric (one number), chart = an
 * aggregate broken down by a group, table = a row list. (Goals aren't reports,
 * so they're tagged `'goal'` at the call site, not here.)
 */
export const reportType = (
  v: Pick<ViewConfig, 'kind' | 'groupBy'>
): Exclude<ReportType, 'goal'> =>
  v.kind === 'list' ? 'table' : v.groupBy ? 'chart' : 'metric'

/**
 * Default icon key for a report with no explicit `icon`, derived from its shape:
 * a list → `listIcon` (cards use 'list', the dashboard builder uses 'table'); an
 * aggregate with a breakdown → 'chart'; a bare aggregate (one number) → 'metric'.
 * Centralizes the fallback so a metric report never falls through to the bar icon.
 */
export function defaultReportIcon(
  config: Pick<ViewConfig, 'kind' | 'groupBy'>,
  listIcon: ReportIconKey = 'list'
): ReportIconKey {
  if (config.kind !== 'aggregate') return listIcon
  return config.groupBy ? 'chart' : 'metric'
}
